/**
 * HelpKit: the blocks a module's guide is written with (the guide opens in HelpPanel from HelpButton / HelpTitle).
 * A guide is a component that returns a Fragment of these blocks, in this order; the panel spaces them out:
 *
 *   import { HelpFlow, HelpIntro, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit"; // from guides/
 *
 *   export default function MatrixGuide() {
 *     const [t] = useTranslation();
 *     return (
 *       <>
 *         <HelpIntro>{t("help.matrix.intro")}</HelpIntro>
 *         <HelpFlow pipeline="bts" current="matrix" />
 *         <HelpSteps>
 *           <HelpStep title={t("help.matrix.steps.add.title")} illustration={<MiniScreen>…</MiniScreen>}>
 *             <HelpText i18nKey="help.matrix.steps.add.text" values={{ button: t("folders.matrix.edit") }} />
 *           </HelpStep>
 *           {canEdit && <HelpStep title={…}>…</HelpStep>}
 *         </HelpSteps>
 *         <HelpLegend rows={[{ key: "draft", label: t("…"), tone: "slate", text: t("help.matrix.legend.draft") }]} />
 *         <HelpRoles rows={[{ key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.matrix.roles.moderator") }]} />
 *         <HelpTips items={[t("help.matrix.tips.copy"), <HelpText key="x" i18nKey="help.matrix.tips.x" values={…} />]} />
 *       </>
 *     );
 *   }
 *
 * - HelpIntro({ children }): the lead paragraph, 1-2 sentences: what this module is for.
 * - HelpFlow({ pipeline: "bts" | "exams", current }): "How it fits together", the chain of modules with `current`
 *   marked "You are here". bts: questions → review → matrix → variants → booklets; exams: questions → tests →
 *   exams → attempts (labels: help.common.pipeline.<key>).
 * - HelpSteps({ children }) + HelpStep({ title, children, illustration }): the numbered steps (5-7, numbered
 *   automatically; put HelpStep elements straight inside, `cond && <HelpStep/>` is fine, no Fragments). A step has
 *   a short title, 1-3 short sentences as children and an illustration made of Mini parts (./Mini.js), shown in a
 *   soft frame and hidden from screen readers: the text must say what the picture shows.
 * - HelpText({ i18nKey, values }): a translated text that may name buttons: in the translation, <ui>{{button}}</ui>
 *   shows the label as a button-like pill (Ui) and <b>…</b> is bold; pass the labels of the real controls from
 *   their existing keys, e.g. values={{ button: t("folders.create_question") }}, so the guide says exactly what the
 *   screen says.
 * - Ui({ children }): that pill, inline, for a UI label in JSX text.
 * - HelpLegend({ rows: [{ key, label, tone, icon, className, badge, text }], title? }): what the marks of the
 *   module mean (statuses: draft / active / archived…): each row a real-size pill and one sentence. The pill is
 *   `label` in CHIP_TONES[tone].badge colours (tone: slate, amber, blue, violet, cyan, green, red; `icon` a leading
 *   icon; `className` instead of `tone` for other colours), or any node as `badge` (e.g. the app's own
 *   QuestionStatusBadge). Default title t("help.common.legend_title").
 * - HelpRoles({ rows: [{ key, role, can, Icon? }], title? }): who can do what (role = its label, e.g.
 *   t("folders.roles.author"); can = one sentence; Icon = the role's heroicon: admin KeyIcon, moderator
 *   ShieldCheckIcon, expert CheckBadgeIcon, author PencilIcon, viewer EyeIcon).
 * - HelpTips({ items, title? }): "Good to know", 2-4 short items (strings or nodes; falsy ones are skipped).
 * - HelpNote({ tone: "info" | "warn", children }): a callout, for one thing that must not be missed.
 * - HelpSection({ title, children }): a titled block for anything else.
 */
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LightBulbIcon,
  SwatchIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { ChevronRightIcon, MapPinIcon } from "@heroicons/react/20/solid";
import { CHIP_TONES } from "../QuestionList/FilterChips";

export const HELP_PIPELINES = {
  bts: ["questions", "review", "matrix", "variants", "booklets"],
  exams: ["questions", "tests", "exams", "attempts"],
};

function SectionTitle({ icon: Icon, iconClassName = "text-slate-400", children }) {
  return (
    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
      {Icon && <Icon className={`h-5 w-5 shrink-0 ${iconClassName}`} aria-hidden="true" />}
      {children}
    </h3>
  );
}

// inline-block: a label moves to the next line whole instead of breaking in two; one longer than the line still
// wraps inside its pill.
export function Ui({ children }) {
  return (
    <span className="inline-block max-w-full rounded-md bg-white px-1.5 py-0.5 text-[0.8125rem] font-semibold leading-tight text-slate-800 shadow-sm ring-1 ring-inset ring-slate-300">
      {children}
    </span>
  );
}

const TAGS = { ui: <Ui />, b: <strong className="font-semibold text-slate-900" /> };

export function HelpText({ i18nKey, values }) {
  return <Trans i18nKey={i18nKey} values={values} components={TAGS} />;
}

export function HelpIntro({ children }) {
  return <p className="text-[15px] leading-relaxed text-slate-700">{children}</p>;
}

export function HelpSection({ title, children }) {
  return (
    <section>
      {title && <SectionTitle>{title}</SectionTitle>}
      <div className="space-y-2 text-sm leading-relaxed text-slate-600">{children}</div>
    </section>
  );
}

// Each link after the first starts with its "›" (in one piece with its chip), so a wrapped line starts with "›"
// instead of the line before ending with one.
export function HelpFlow({ pipeline = "bts", current }) {
  const [t] = useTranslation();
  const keys = HELP_PIPELINES[pipeline] || HELP_PIPELINES.bts;
  return (
    <section className="rounded-2xl bg-slate-50 p-4 ring-1 ring-inset ring-slate-200/70">
      <SectionTitle>{t("help.common.flow_title")}</SectionTitle>
      <ol className="flex flex-wrap items-start gap-x-1 gap-y-2.5">
        {keys.map((key, index) => {
          const here = key === current;
          return (
            <li key={key} className="flex items-start gap-1 whitespace-nowrap" aria-current={here ? "step" : undefined}>
              {index > 0 && <ChevronRightIcon className="mt-1.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />}
              <span className="flex flex-col items-center gap-1">
                <span
                  className={`inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-xs ring-1 ring-inset ${
                    here ? "bg-blue-600 font-semibold text-white ring-blue-600" : "bg-white font-medium text-slate-600 ring-slate-200"
                  }`}
                >
                  {here && <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                  {t(`help.common.pipeline.${key}`)}
                </span>
                {here && <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">{t("help.common.you_are_here")}</span>}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export function HelpSteps({ children }) {
  let number = 0;
  const steps = React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((step) => React.cloneElement(step, { number: step.props.number ?? (number += 1) }));
  return <ol className="space-y-3">{steps}</ol>;
}

export function HelpStep({ number, title, children, illustration }) {
  const [t] = useTranslation();
  return (
    <li className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      {/* relative: keeps the sr-only step number inside the panel's scroll box. */}
      <h3 className="relative flex items-start gap-2.5 text-[15px] font-semibold leading-6 text-slate-900">
        {number != null && (
          <>
            <span
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold tabular-nums text-white"
              aria-hidden="true"
            >
              {number}
            </span>
            <span className="sr-only">{t("help.common.step", { n: number })}: </span>
          </>
        )}
        <span className="min-w-0">{title}</span>
      </h3>
      {children && <div className="mt-1.5 space-y-1.5 text-sm leading-relaxed text-slate-600 sm:pl-[2.125rem]">{children}</div>}
      {illustration && (
        <div className="mt-3 select-none overflow-hidden rounded-xl bg-slate-50 p-3" aria-hidden="true">
          {illustration}
        </div>
      )}
    </li>
  );
}

// A list of terms (a pill) and what they mean or allow, one row each: under each other on phones, side by side
// from sm up.
function TermList({ icon, title, rows }) {
  return (
    <section>
      <SectionTitle icon={icon}>{title}</SectionTitle>
      <dl className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white px-4">
        {rows.map(({ key, term, text }, index) => (
          <div key={key ?? index} className="py-3 sm:flex sm:items-start sm:gap-4">
            <dt className="shrink-0 sm:w-40">{term}</dt>
            <dd className="mt-1.5 min-w-0 text-sm leading-relaxed text-slate-600 sm:mt-0">{text}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function HelpLegend({ rows = [], title }) {
  const [t] = useTranslation();
  const list = rows.filter(Boolean);
  if (!list.length) return null;
  return (
    <TermList
      icon={SwatchIcon}
      title={title || t("help.common.legend_title")}
      rows={list.map(({ key, label, tone, icon: Icon, className = "", badge, text }) => ({
        key,
        text,
        term: badge || (
          <span
            className={`inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
              tone || !className ? (CHIP_TONES[tone] || CHIP_TONES.slate).badge : ""
            } ${className}`}
          >
            {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
            <span className="min-w-0 break-words">{label}</span>
          </span>
        ),
      }))}
    />
  );
}

export function HelpRoles({ rows = [], title }) {
  const [t] = useTranslation();
  const list = rows.filter(Boolean);
  if (!list.length) return null;
  return (
    <TermList
      icon={UserGroupIcon}
      title={title || t("help.common.roles_title")}
      rows={list.map(({ key, role, can, Icon }) => ({
        key,
        text: can,
        term: (
          <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-500/15">
            {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
            <span className="truncate">{role}</span>
          </span>
        ),
      }))}
    />
  );
}

export function HelpTips({ items = [], title }) {
  const [t] = useTranslation();
  const list = items.filter(Boolean);
  if (!list.length) return null;
  return (
    <section className="rounded-2xl bg-slate-50 p-4 ring-1 ring-inset ring-slate-200/70">
      <SectionTitle icon={LightBulbIcon} iconClassName="text-amber-500">
        {title || t("help.common.tips_title")}
      </SectionTitle>
      <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
        {list.map((item, index) => (
          <li key={index} className="flex gap-2.5">
            <span className="mt-[0.5625rem] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" aria-hidden="true" />
            <span className="min-w-0">{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function HelpNote({ tone = "info", children }) {
  const warn = tone === "warn";
  const Icon = warn ? ExclamationTriangleIcon : InformationCircleIcon;
  return (
    <div
      role="note"
      className={`flex gap-2.5 rounded-xl p-3 text-sm leading-relaxed ring-1 ring-inset ${
        warn ? "bg-amber-50 text-amber-900 ring-amber-600/20" : "bg-blue-50 text-blue-900 ring-blue-600/15"
      }`}
    >
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${warn ? "text-amber-600" : "text-blue-600"}`} aria-hidden="true" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}
